import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { api, withQuery }        from "./helpers/api"
import { t }                     from "./helpers/format"
import { rangeFor, shiftRange, today } from "./helpers/dates"
import { dayKey, isApprovable, splitKey } from "./helpers/states"

import Animations    from "./components/Animations"
import Header        from "./components/Header"
import Toolbar       from "./components/Toolbar"
import KpiStrip      from "./components/KpiStrip"
import Legend        from "./components/Legend"
import Grid          from "./components/Grid"
import GridSkeleton  from "./components/GridSkeleton"
import SelectionBar  from "./components/SelectionBar"
import CloseModal    from "./components/CloseModal"
import Toast         from "./components/Toast"
import DayDrawer     from "./drawer/DayDrawer"

const filtersOf = board => ({
  from:       board.from,
  to:         board.to,
  manager_id: board.filters?.manager_id || "",
  project_id: board.filters?.project_id || "",
  status:     board.filters?.status || "",
})

const newlyApproved = (before, after) => {
  const keys = new Set()
  const previous = new Map((before?.rows || []).map(row => [row.user.id, row.cells]))
  ;(after?.rows || []).forEach(row => {
    Object.entries(row.cells).forEach(([date, cell]) => {
      const old = previous.get(row.user.id)?.[date]
      if (cell.state === "approved" && old && old.state !== "approved") keys.add(dayKey(row.user.id, date))
    })
  })
  return keys
}

const App = props => {
  const { json_url, approve_many_url, close_url, export_url } = props

  const [board,    setBoard]    = useState(props.board)
  const [filters,  setFilters]  = useState(() => filtersOf(props.board))
  const [mode,     setMode]     = useState(props.board.days.length > 7 ? "month" : "week")
  const [loading,  setLoading]  = useState(false)
  const [busy,     setBusy]     = useState(false)
  const [selected, setSelected] = useState(() => new Set())
  const [flash,    setFlash]    = useState(() => new Set())
  const [drawer,   setDrawer]   = useState(null)
  const [closing,  setClosing]  = useState(false)
  const [toast,    setToast]    = useState(null)

  const boardRef   = useRef(board)
  const requestRef = useRef(0)
  boardRef.current = board

  const notify = useCallback((message, tone = "success") => setToast({ message, tone, at: Date.now() }), [])

  const load = useCallback((next = filters) => {
    const ticket = ++requestRef.current
    setLoading(true)
    return api.get(json_url, next)
      .then(response => {
        if (ticket !== requestRef.current) return
        const approved = newlyApproved(boardRef.current, response)
        setBoard(response)
        if (approved.size) setFlash(approved)
      })
      .catch(error => notify(error.message, "error"))
      .finally(() => { if (ticket === requestRef.current) setLoading(false) })
  }, [filters, json_url, notify])

  useEffect(() => {
    if (!flash.size) return
    const timer = setTimeout(() => setFlash(new Set()), 1600)
    return () => clearTimeout(timer)
  }, [flash])

  const applyFilters = patch => {
    const next = { ...filters, ...patch }
    setFilters(next)
    setSelected(new Set())
    load(next)
  }

  const goTo = range => applyFilters(range)
  const switchMode = nextMode => {
    if (nextMode === mode) return
    setMode(nextMode)
    goTo(rangeFor(nextMode, nextMode === "month" ? filters.from : (filters.from <= today() && today() <= filters.to ? today() : filters.from)))
  }

  const approvableKeys = useMemo(() => {
    const keys = []
    board.rows.forEach(row => Object.entries(row.cells).forEach(([date, cell]) => {
      if (isApprovable(cell)) keys.push(dayKey(row.user.id, date))
    }))
    return keys
  }, [board])

  const signedKeys = useMemo(() => {
    const keys = []
    board.rows.forEach(row => Object.entries(row.cells).forEach(([date, cell]) => {
      if (cell.state === "signed") keys.push(dayKey(row.user.id, date))
    }))
    return keys
  }, [board])

  useEffect(() => {
    const allowed = new Set(approvableKeys)
    setSelected(current => {
      const kept = [...current].filter(key => allowed.has(key))
      return kept.length === current.size ? current : new Set(kept)
    })
  }, [approvableKeys])

  const toggleKeys = (keys, on) => setSelected(current => {
    const next = new Set(current)
    keys.forEach(key => (on ? next.add(key) : next.delete(key)))
    return next
  })

  const approveMany = keys => {
    if (!keys.length || busy) return
    setBusy(true)
    api.post(approve_many_url, {}, { days: keys.map(splitKey) })
      .then(({ approved, skipped }) => {
        notify(skipped
          ? t("toasts.approved_skipped", { count: approved, skipped })
          : t("toasts.approved", { count: approved }), approved ? "success" : "error")
        setSelected(new Set())
        return load()
      })
      .catch(error => notify(error.message, "error"))
      .finally(() => setBusy(false))
  }

  const closePeriod = () => {
    setBusy(true)
    return api.post(close_url, {}, { to: board.to })
      .then(({ closed }) => {
        notify(t("toasts.closed", { count: closed }))
        setClosing(false)
        return load()
      })
      .catch(error => notify(error.message, "error"))
      .finally(() => setBusy(false))
  }

  const exportUrl = scope => withQuery(export_url, { ...filters, scope })

  const openDay = (user, date) => setDrawer({ user, date })

  return (
    <div className="pb-24 font-sans">
      <Animations />

      <Header
        board           = {board}
        exportUrl       = {exportUrl}
        planningUrl     = {props.planning_url}
        preferencesUrl  = {props.preferences_url}
        onClose         = {() => setClosing(true)}
      />

      <Toolbar
        board        = {board}
        filters      = {filters}
        mode         = {mode}
        loading      = {loading}
        signedCount  = {signedKeys.length}
        busy         = {busy}
        onMode       = {switchMode}
        onPrev       = {() => goTo(shiftRange(mode, filters.from, -1))}
        onNext       = {() => goTo(shiftRange(mode, filters.from, 1))}
        onToday      = {() => goTo(rangeFor(mode, today()))}
        onFilter     = {applyFilters}
        onApproveSigned = {() => approveMany(signedKeys)}
      />

      <KpiStrip kpis={board.kpis} />

      <div className="px-7">
        <Legend weeklyMax={board?.settings?.weekly_max_hours} />

        {loading && (board.from !== filters.from || board.to !== filters.to)
          ? <GridSkeleton days={rangeDays(filters)} />
          : (
            <Grid
              board       = {board}
              mode        = {mode}
              selected    = {selected}
              flash       = {flash}
              active      = {drawer}
              planningUrl = {props.planning_url}
              onToggle    = {toggleKeys}
              onOpen      = {openDay}
              onApproveRow = {keys => approveMany(keys)}
              busy        = {busy}
            />
          )}
      </div>

      <SelectionBar
        count     = {selected.size}
        busy      = {busy}
        onClear   = {() => setSelected(new Set())}
        onApprove = {() => approveMany([...selected])}
      />

      <DayDrawer
        target   = {drawer}
        board    = {board}
        urls     = {props}
        onClose  = {() => setDrawer(null)}
        onMove   = {date => setDrawer(current => current && { ...current, date })}
        onChange = {() => load()}
        notify   = {notify}
      />

      <CloseModal
        isOpen    = {closing}
        to        = {board.to}
        busy      = {busy}
        onCancel  = {() => setClosing(false)}
        onConfirm = {closePeriod}
      />

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  )
}

const rangeDays = ({ from, to }) => {
  const a = new Date(`${from}T00:00:00`)
  const b = new Date(`${to}T00:00:00`)
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
}

export default App
